import { html, nothing } from "lit";
import type { CSSResultGroup } from "lit";

import { customElement, property, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { live } from "lit/directives/live.js";

import { textareaStyles } from "./textarea.css.ts";

import "../field-wrapper/field-wrapper.ts";
import { FormElement } from "../form-element.ts";

@customElement("wow-textarea")
export class Textarea extends FormElement {
  static override styles: CSSResultGroup = [textareaStyles];

  @query("textarea") private textareaElement!: HTMLTextAreaElement;

  @property({ type: String }) label = "";
  @property({ attribute: "help-text" }) helpText = "";
  @property({ type: String }) placeholder = "";
  @property({ type: Number }) rows: number = 0;

  @property({ type: Boolean }) autogrow = false;

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, attribute: "show-required-indicator" }) showRequiredIndicator = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) readonly = false;

  @property({ type: Number }) minlength?: number;
  @property({ type: Number }) maxlength?: number;
  @property({ type: String }) inputId?: string;

  protected override firstUpdated(changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated?.(changedProperties);
    this.autoGrow();
  }

  protected override updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated?.(changedProperties);
    if (changedProperties.has("value") && this.autogrow) {
      this.autoGrow();
    }
  }

  public setError(message: string) {
    this._internals.setValidity({ customError: true }, message, this.textareaElement);
  }

  public clearError() {
    this._internals.setValidity({});
  }

  public get input(): HTMLTextAreaElement {
    return this.textareaElement;
  }

  private autoGrow() {
    if (!this.autogrow || !this.textareaElement) return;

    const el = this.textareaElement;

    // Always reset to auto first to get the natural scrollHeight
    el.style.height = "auto";

    // Then set to scrollHeight
    el.style.height = `${el.scrollHeight}px`;
  }

  private validateInput(): void {
    if (!this.textareaElement) return;

    const validity = this.textareaElement.validity;
    const isValid = validity.valid && !this.customError;

    this.hasError = !isValid;

    if (!isValid) {
      this.errorMessage = this.customError || this.textareaElement.validationMessage;
      this._internals.setValidity(this.customError ? { customError: true } : validity, this.errorMessage);
    } else {
      this.errorMessage = "";
      this._internals.setValidity({});
    }
  }

  private handleInput(e: Event) {
    this.value = (e.target as HTMLTextAreaElement).value;
    this.autoGrow();
  }

  private handleFocus(): void {
    this.focused = true;
  }

  private handleBlur(): void {
    this.focused = false;
    this.validateInput();
  }

  private handleInvalid(event: Event): void {
    event.preventDefault();
    this.validateInput();
  }

  override render() {
    const currentLength = (this.value as string)?.length || 0;

    const textareaClasses = {
      textarea: true,
      "with-placeholder": !!this.value,
      invalid: this.validationMessage,
    };

    const placeholderClasses = {
      "input-placeholder": true,
      active: !!this.value,
    };

    const charCountClasses = {
      "char-count": true,
      active: !!this.value,
    };

    const placeholderWrapperClasses = {
      "placeholder-wrapper": true,
      active: !!this.value,
    };

    return html`
      <wow-field-wrapper>
        ${this.label
          ? html`<label class="label" slot="label" for=${this.id}
              >${this.label}${this.showRequiredIndicator
                ? html`<span class="required-indicator">*</span>`
                : nothing}</label
            >`
          : nothing}
        ${this.helpText ? html`<span slot="help" class="help-text">${this.helpText}</span>` : nothing}

        <div class="textarea-wrapper" slot="control">
          <textarea
            class=${classMap(textareaClasses)}
            part="textarea"
            title=${this.title}
            name=${ifDefined(this.name)}
            ?disabled=${this.disabled}
            ?readonly=${this.readonly}
            ?required=${this.required}
            minlength=${ifDefined(this.minlength)}
            maxlength=${ifDefined(this.maxlength)}
            rows=${this.rows}
            .value=${live(this.value as string)}
            ?autofocus=${this.autofocus}
            aria-describedby=${ifDefined(this.id + "-desc")}
            aria-invalid=${!!this.validationMessage}
            @input=${this.handleInput}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
          ></textarea>

          <div class=${classMap(placeholderWrapperClasses)}>
            ${this.placeholder
              ? html`<label class=${classMap(placeholderClasses)} for=${this.id}> ${this.placeholder} </label>`
              : nothing}
            ${this.maxlength
              ? html`<div class=${classMap(charCountClasses)}>${currentLength}/${this.maxlength}</div>`
              : ""}
          </div>
        </div>

        ${this.validationMessage
          ? html`<span class="error-message" slot="error">${this.validationMessage}</span>`
          : nothing}
      </wow-field-wrapper>
    `;
  }
}
